import * as React from "react";
import {
  StyleSheet,
  FlatList,
  ActivityIndicator,
  RefreshControl,
  Platform,
  TouchableOpacity,
} from "react-native";
import { View, Text } from "../../Themed";
import Colors from "../../../constants/Colors";
import OpenHouseListItem from "../../OpenHouseListItem";
import { useNavigation } from "@react-navigation/native";
import GlobalContext from "../../../context/Global";
import PageHeader from "../../PageHeader";
import Icon from "../../Icon";
import { calculateHoursBetween } from "../../../utils/CalculateHoursBetween";
import { useGetOpenHouses } from "../../../hooks/useGetOpenHouses";
import useNetworkHealth from "../../../hooks/useNetworkHealth";

export default function ViewAllOpenHouses({ route }: any) {
  const { goBack, navigate } = useNavigation();
  const { openHouseParams, status } = route.params;
  const { user } = React.useContext(GlobalContext);
  const network = useNetworkHealth();

  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
    isFetched,
    isLoading,
  } = useGetOpenHouses(openHouseParams?.status, user?.openHouseUuid);

  const openHouses = React.useMemo(() => {
    const houses = network.latencyMs ?? null
      ? data?.pages?.flatMap((page) => page?.openHouses || [])
      : route?.params?.openHouses || [];

    return houses;
  }, [network.latencyMs ?? null, data?.pages, route?.params?.openHouses]);

  const openHouseListItemPressed = (item) => {
    navigate("OpenHomeDetails", { openHouse: item });
  };

  const getTitle = () => {
    switch (status) {
      case "upcoming":
        return "Upcoming";
      case "active":
        return "Current";
      case "completed":
        return "Completed";
    }
  };

  const BackButton = () => (
    <Icon
      name="arrow-left"
      size={24}
      color={Colors.primary_500}
      onPress={() => goBack()}
    />
  );

  const renderFooter = () => {
    // Show loading spinner while fetching
    if (isFetchingNextPage) {
      return (
        <View
          style={{
            marginVertical: 20,
          }}
        >
          <ActivityIndicator size="large" color={Colors.primary_500} />
        </View>
      );
    }

    // Show "Load More" button on web if there are more pages
    if (Platform.OS === "web" && hasNextPage) {
      return (
        <View
          style={{
            marginVertical: 20,
            alignItems: "center",
            backgroundColor: 'transparent',
            width: '100%',
          }}
        >
          <TouchableOpacity
            onPress={() => fetchNextPage()}
            style={{
              backgroundColor: Colors.primary_500,
              paddingHorizontal: 32,
              paddingVertical: 14,
              borderRadius: 8,
              minWidth: 150,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text
              size={16}
              weight="SemiBold"
              style={{ color: 'white' }}
            >
              Load More
            </Text>
          </TouchableOpacity>
        </View>
      );
    }

    return null;
  };

  return (
    <View style={[styles.container]}>
      <PageHeader
        leftComponent={<BackButton />}
        centerComponent={
          <View
            darkColor={Colors.nuetral_grey_700}
            style={styles.pageTitleContainer}
          >
            <Text
              size={18}
              lineHeight={24}
              weight="SemiBold"
              lightColor={Colors.nuetral_grey_700}
            >
              {getTitle()} Open Houses
            </Text>
          </View>
        }
      />
      <FlatList
        data={openHouses}
        keyExtractor={(item, index) => index.toString()}
        renderItem={({ item }) => (
          <>
            <OpenHouseListItem
              address={item?.propertyDetails}
              date={item?.start_date}
              completed={status === "completed"}
              attendees={item?.manual_attendee_count || 0}
              hours={
                item?.completed_at
                  ? calculateHoursBetween(item?.start_time, item?.end_time)
                  : 0
              }
              startTime={item?.start_time}
              endTime={item?.end_time}
              onPress={() => openHouseListItemPressed(item)}
              item={item}
            />
            <View style={styles.space} />
          </>
        )}
        onEndReached={() => {
          if (hasNextPage) fetchNextPage();
        }}
        style={
          Platform.OS === "web" ? { width: "75%", alignSelf: "center" } : null
        }
        onEndReachedThreshold={0.5}
        ListFooterComponent={renderFooter}
        refreshControl={
          <RefreshControl
            refreshing={isFetched ? isLoading : false}
            onRefresh={refetch}
          />
        }
      />
    </View>
  );
}

const styles = StyleSheet.create({
  pageTitleContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
  container: {
    backgroundColor: Colors.background,
    gap: 24,
    height: "100%",
  },
  space: { height: 8 },
});
